import React, { useState, useEffect } from 'react';

/**
 * Inactivity Warning Dialog
 * Shows a warning when user is about to be logged out due to inactivity
 * Supports role-specific messaging for User, Admin, and Nominee sessions
 */
const InactivityWarning = ({ show, secondsRemaining, onStayLoggedIn, onLogout, roleType = 'User' }) => {
  const [countdown, setCountdown] = useState(secondsRemaining);

  useEffect(() => {
    if (show && secondsRemaining > 0) {
      setCountdown(secondsRemaining);
      
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [show, secondsRemaining]);

  if (!show) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
    >
      <div className="bg-[#0a0a0f] border border-yellow-500/30 p-8 rounded-2xl max-w-md w-full my-auto max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem)] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-yellow-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center mb-4 text-white">
          Session Timeout Warning
        </h2>

        <p className="text-gray-300 text-center mb-6">
          You've been inactive for a while. For your security, you'll be automatically logged out in:
        </p>

        <div className="text-center mb-8">
          <div className="text-6xl font-bold text-yellow-400 mb-2">
            {countdown}
          </div>
          <div className="text-sm text-gray-400">
            {countdown === 1 ? 'second' : 'seconds'}
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onStayLoggedIn}
            className="flex-1 bg-[#0A2472] hover:bg-[#0d2d8a] text-white py-3 rounded-lg text-lg font-semibold transition"
          >
            Stay Logged In
          </button>
          <button
            onClick={onLogout}
            className="flex-1 bg-white/5 border border-white/10 py-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all text-lg font-semibold"
          >
            Logout Now
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Any activity will keep you logged in automatically
        </p>
      </div>
    </div>
  );
};

export default InactivityWarning;
